import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authzApi, type Role } from '../../api/authz';

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      setRoles(await authzApi.listRoles());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await authzApi.createRole({ name, description });
      setName('');
      setDescription('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Link to="/authorization" className="text-sm text-slate-600 hover:underline">← Authorization dashboard</Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Roles</h1>
      <p className="mt-1 text-sm text-slate-600">Manage RBAC roles and hierarchy.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <form onSubmit={onCreate} className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-3">
        <input required placeholder="Role name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 md:col-span-2" />
        <button type="submit" className="rounded-lg bg-slate-900 px-4 py-2 text-white md:col-span-3 md:w-fit">Create role</button>
      </form>

      {loading ? (
        <p className="mt-6 text-slate-600">Loading…</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {roles.map((r) => (
            <li key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-900">{r.name}</p>
                  <p className="text-sm text-slate-600">{r.description || '—'}</p>
                  {r.parent_role_id && <p className="mt-1 text-xs text-slate-500">Parent: {r.parent_role_id}</p>}
                  <p className="mt-2 text-xs text-slate-500">{r.permissions?.length ?? 0} permissions</p>
                </div>
                {!r.id.startsWith('role-') && (
                  <button type="button" onClick={() => void authzApi.deleteRole(r.id).then(load)} className="text-sm text-red-600 hover:underline">
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
